<script setup>
/**
 * The session panel's Files (prototype, Scenario → In session → Files:
 * filesPane and its "Nothing shared yet" override).
 *
 * Every row is the file's type glyph, its whole name, "PDF · 1.2 MB" and a
 * download. Whoever runs the room also sees who each file is shared with, and
 * a row menu — Download, Rename, Change who can see it, Remove (asked first) —
 * with Share a file at the foot. A policyholder sees the files and can
 * download them, and nothing that suggests they could change one.
 *
 * QA (In-session Files → the buttons do nothing): the old pane was the hub's
 * Resources page squeezed into the panel — bulk bin / download / ✕ over tick
 * boxes that never reached them, the storage ring (read as a spinner that
 * never ends), a pager whose count was clipped to a lone ‹, and downloads by
 * `src`, which a session's files arrive without. None of that is here.
 *
 * On a phone it sits in the room's Files sheet (prototype sheetFiles, Mobile
 * Room): each row is the file's type in a coloured badge, its name on one
 * line, "size · who shared it", and View and Download. View opens it full
 * screen over the room (MobileFileViewer) while the session goes on, and a
 * download says so when it is done. A file somebody else shares is told to
 * the room (files-shared) for the count on Files and the notice with Open.
 */
import axios from 'axios';
import { computed, onMounted, ref, useId, watch } from 'vue';
import Loading from './shared/loading/Loading.vue';
import { downloadErrorMessage, downloadFailureIsStale, getFileType } from '../../helpers/helper';
import IconDoc from '../icons/IconDoc.vue';
import IconImage from '../icons/IconImage.vue';
import IconPdf from '../icons/IconPdf.vue';
import IconPpt from '../icons/IconPpt.vue';
import IconTxt from '../icons/IconTxt.vue';
import { useSocketStore } from '../../stores/socketStore';
import useSocketListeners from '../../composables/useSocketListeners.js';
import { useAuthStore } from '../../stores/auth';
import { CAPABILITIES, can } from '../../helpers/roles';
import ResourceAccessTag from '../resource-access/ResourceAccessTag.vue';
import { bookFor, inAnotherBook } from '../../composables/useResourceAccess';
import ManageAccessModal from '../resource-access/ManageAccessModal.vue';
import ApplyToAllConfirm from '../resource-access/ApplyToAllConfirm.vue';
import useHubMembers from '../../composables/useHubMembers';
import useResourceDownload from '../../composables/useResourceDownload';
import { sizeLabel, typeSize } from '../../composables/usePortalLibrary';
import ContentRowMenu from '../content/ContentRowMenu.vue';
import SessionDialog from '../../livekit-client/components/SessionDialog.vue';
import MobileFileViewer from '../../livekit-client/components/MobileFileViewer.vue';
import XlButton from '../xl/XlButton.vue';
import XlField from '../xl/XlField.vue';

const props = defineProps({
  // 'admin-settings' for whoever runs the room; anything else reads only.
  type: { type: String, default: '' },
  event: { type: Object, required: true },
  actionsDisabled: Boolean,
  // The room is on a phone: its Files sheet.
  phone: Boolean,
});

// files-shared: the names of files somebody else just shared (a phone only).
// downloaded: a file's name once its download is handed to the phone.
const emit = defineEmits(['files-shared', 'downloaded']);

const ACCEPT_EXTENSIONS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'txt', 'jpg', 'jpeg', 'png', 'gif'];
const ACCEPT = 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/jpeg,image/png,image/gif,text/plain,application/pdf';
// The API pages at most 100; a session's files rarely reach one page.
const PER_PAGE = 100;
const GB = 1024 ** 3;

const api = () => window.store.event_backend_api;
const socketStore = useSocketStore();
const socketListeners = useSocketListeners();
const authStore = useAuthStore();
const { download } = useResourceDownload();
const { members: hubMembers, loadHubMembers } = useHubMembers();

const admin = computed(() => props.type === 'admin-settings');

const files = ref([]);
const total = ref(0);
const page = ref(1);
const lastPage = computed(() => Math.max(1, Math.ceil(total.value / PER_PAGE)));
// Hub-wide bytes in use and the plan's allowance, for the check before an upload.
const usedBytes = ref(0);
const capacityGb = ref(null);

const loading = ref(true);
const failed = ref(false);
const uploading = ref(false);
const downloadingId = ref(null);
const busy = ref(false);

// Later uploads join the same media item: the session's own collection.
const sessionMedia = ref(props.event.during_session_media_model ?? null);

const fileInput = ref(null);
// The picker's own id, for its label: unique per pane, from Vue (no randomness needed).
const fileInputId = `session-files-input-${useId()}`;
const renaming = ref(null);
const renameValue = ref('');
const renameError = ref('');
const removing = ref(null);
const accessResource = ref(null);
const pendingApplyAll = ref(null);
// The file open in the phone's viewer.
const viewing = ref(null);

const notify = (message, error = false) => window.loadNotification?.({ message, error });

const messageFrom = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message) return message;
  if (message && typeof message === 'object') {
    const first = Object.values(message).flat()[0];
    if (first) return first;
  }
  return fallback;
};

const iconFor = (resource) => {
  switch (getFileType(resource.type)) {
    case 'pdf': return IconPdf;
    case 'doc': return IconDoc;
    case 'txt': return IconTxt;
    case 'ppt': return IconPpt;
    default: return IconImage;
  }
};

// The phone's badge: the prototype's four kinds, and plain text as a document.
const BADGES = { pdf: ['pdf', 'PDF'], doc: ['doc', 'DOC'], txt: ['doc', 'TXT'], ppt: ['ppt', 'PPT'] };
const badgeFor = (resource) => {
  const [kind, label] = BADGES[getFileType(resource.type)] ?? ['img', 'IMG'];
  return { kind, label };
};
// "1.2 MB · Dana Wells"
const phoneMeta = (resource) => [sizeLabel(resource.size), resource.shared_by].filter(Boolean).join(' · ');

// The viewer opens over the whole room, not inside the sheet.
const viewerHost = () => document.querySelector('.session-wrapper.ses-phone') || 'body';

// A broker who moderates this session manages its files, and the API marks
// which by attaching `access` to them. With MANAGE_HUB_CONTENT every row is theirs.
const contributesOnly = computed(() =>
  can(authStore.userRole, CAPABILITIES.CONTRIBUTE_HUB_CONTENT, authStore.capabilities)
  && !can(authStore.userRole, CAPABILITIES.MANAGE_HUB_CONTENT, authStore.capabilities)
);
const manageable = (resource) => admin.value && (!contributesOnly.value || Boolean(resource.access));
const editable = (resource) => manageable(resource) && !inAnotherBook(resource);
const changeable = computed(() => files.value.filter(editable));
const leftOut = computed(() => files.value.filter(manageable).length - changeable.value.length);

const menuItems = (resource) => [
  { key: 'download', label: 'Download', disabled: downloadingId.value === resource.id },
  { key: 'rename', label: 'Rename', disabled: props.actionsDisabled },
  { key: 'access', label: 'Change who can see it', disabled: props.actionsDisabled },
  { key: 'remove', label: 'Remove', danger: true, disabled: props.actionsDisabled },
];

const onMenu = (resource, key) => {
  if (key === 'download') onDownload(resource);
  else if (key === 'rename') openRename(resource);
  else if (key === 'access') accessResource.value = resource;
  else if (key === 'remove') removing.value = resource;
};

const load = async (to = page.value) => {
  failed.value = false;
  try {
    const { data } = await axios.get(`${api()}/resources`, {
      params: { page: to, per_page: PER_PAGE, session_id: props.event.id },
    });
    files.value = data?.data ?? [];
    total.value = data?.meta?.total ?? files.value.length;
    usedBytes.value = Number(data?.total_size) || 0;
    page.value = to;
  } catch (error) {
    console.error(error);
    failed.value = true;
  } finally {
    loading.value = false;
  }
};

const loadCapacity = async () => {
  try {
    const { data } = await axios.get(`${api()}/subdomain-data`);
    const storage = (data?.data?.features ?? []).find((feature) => feature.group_name === 'storage');
    const gb = Number.parseInt(storage?.value, 10);
    capacityGb.value = Number.isFinite(gb) ? gb : null;
  } catch (error) {
    // Without the allowance the upload is still made; the check is a courtesy.
    console.error(error);
  }
};

const onDownload = async (resource) => {
  if (downloadingId.value) return;
  downloadingId.value = resource.id;
  try {
    await download(resource);
    if (props.phone) emit('downloaded', resource.name);
  } catch (error) {
    console.error(error);
    notify(downloadErrorMessage(error), true);
    // The file is gone or now off-limits; re-read so it stops being offered.
    if (downloadFailureIsStale(error)) await load();
  } finally {
    downloadingId.value = null;
  }
};

const chooseFiles = () => {
  if (props.actionsDisabled || uploading.value) return;
  if (fileInput.value) {
    fileInput.value.value = null;
    fileInput.value.click();
  }
};

const onFilesChosen = async (e) => {
  const chosen = Array.from(e.target.files ?? []);
  if (!chosen.length || props.actionsDisabled) return;

  if (chosen.some((file) => !ACCEPT_EXTENSIONS.includes(file.name.split('.').pop().toLowerCase()))) {
    notify('Only PDF, Word, PowerPoint, text and image files (JPG, PNG, GIF) can be shared.', true);
    return;
  }
  const adding = chosen.reduce((sum, file) => sum + file.size, 0);
  if (capacityGb.value && usedBytes.value + adding > capacityGb.value * GB) {
    notify('There is no room left in the hub’s storage for this.', true);
    return;
  }

  uploading.value = true;
  try {
    const formData = new FormData();
    formData.append('multiple', true);
    if (sessionMedia.value) formData.append('media_item_id', sessionMedia.value);
    chosen.forEach((file) => formData.append('file[]', file));
    const { data } = await axios.post(`${api()}/media-items`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const mediaItemId = data.data.id;
    sessionMedia.value = mediaItemId;

    await axios.put(`${api()}/session-resources/${props.event.id}`, {
      during_session_media_model: mediaItemId,
    });

    // Everybody in the room re-reads their own list (resources_refresh).
    socketStore.emit('new_session_resources', {
      domain: window.store.subdomain,
      session_id: props.event.id,
    });

    await load(1);
    notify(chosen.length === 1 ? 'Shared with the room' : `${chosen.length} files shared with the room`);
  } catch (error) {
    console.error(error);
    notify(messageFrom(error, 'The file could not be shared. Try again.'), true);
  } finally {
    uploading.value = false;
  }
};

const openRename = (resource) => {
  renaming.value = resource;
  renameValue.value = resource.name || '';
  renameError.value = '';
};

const saveRename = async () => {
  const name = renameValue.value.trim();
  if (!name) {
    renameError.value = 'Give the file a name.';
    return;
  }
  busy.value = true;
  try {
    const { data } = await axios.patch(`${api()}/media-items/${renaming.value.id}`, { name });
    renaming.value.name = data?.data?.name ?? name;
    renaming.value = null;
    notify('File renamed');
  } catch (error) {
    console.error(error);
    renameError.value = messageFrom(error, 'The file could not be renamed.');
  } finally {
    busy.value = false;
  }
};

const confirmRemove = async () => {
  const resource = removing.value;
  busy.value = true;
  try {
    await axios.post(`${api()}/media-items/delete`, {
      resources: [{ media_id: resource.id, session_id: resource.session_id ?? props.event.id }],
      type: 'global_resources',
    });
    removing.value = null;
    socketStore.emit('new_session_resources', {
      domain: window.store.subdomain,
      session_id: props.event.id,
    });
    // The last file of a later page takes its page with it.
    await load(files.value.length === 1 && page.value > 1 ? page.value - 1 : page.value);
    notify('File removed');
  } catch (error) {
    console.error(error);
    notify(messageFrom(error, 'The file could not be removed.'), true);
  } finally {
    busy.value = false;
  }
};

const handleAccessSaved = (access) => {
  const row = files.value.find((resource) => resource.id === accessResource.value?.id);
  if (row) row.access = access;
  accessResource.value = null;
};

const confirmApplyAll = async () => {
  const access = pendingApplyAll.value;
  const mediaIds = changeable.value.map((resource) => resource.id);
  busy.value = true;
  try {
    if (mediaIds.length) {
      await axios.put(`${api()}/resources/access/bulk`, { media_ids: mediaIds, ...access });
      files.value.forEach((resource) => {
        if (mediaIds.includes(resource.id)) resource.access = { ...access };
      });
    }
  } catch (error) {
    console.error(error);
    notify(messageFrom(error, 'Who can see these could not be changed.'), true);
  } finally {
    busy.value = false;
    pendingApplyAll.value = null;
    accessResource.value = null;
  }
};

onMounted(async () => {
  // The broadcast carries no viewer, so each client re-reads its own scoped list.
  // On a phone, what is new in it — not on the first read, and never this
  // client's own share, which the relay does not send back — is told to the room.
  socketListeners.on('resources_refresh', async () => {
    const read = !loading.value && !failed.value;
    const known = new Set(files.value.map((resource) => resource.id));
    await load();
    if (!props.phone || !read || failed.value) return;
    const added = files.value.filter((resource) => !known.has(resource.id)).map((resource) => resource.name);
    if (added.length) emit('files-shared', added);
  });

  await load(1);
});

// The pane stays mounted while the panel is open, so somebody made a moderator
// mid-session gets what an admin needs then, and the list's access tags with it.
watch(admin, (isAdmin, was) => {
  if (!isAdmin) return;
  loadCapacity();
  loadHubMembers();
  if (was === false) load();
}, { immediate: true });

// Back on a wide screen there is no viewer; a file taken away closes it.
watch(() => props.phone, (phone) => { if (!phone) viewing.value = null; });
watch(files, (list) => {
  if (viewing.value && !list.some((resource) => resource.id === viewing.value.id)) viewing.value = null;
});
</script>

<template>
  <div class="rf" :class="{ 'rf--phone': phone }" data-testid="session-files">
    <Loading v-if="loading" variant="rows" :rows="3" class="rf__loading" />

    <div v-else-if="failed" class="rf__empty" data-testid="session-files-failed">
      <span class="rf__empty-glyph" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4.4 3.6 19.6h16.8z" /><path d="M12 10v3.8M12 16.8h.01" /></svg>
      </span>
      <b>The files didn't load</b>
      <p>Check your connection and try again.</p>
      <XlButton size="sm" variant="ghost" @click="load()">Try again</XlButton>
    </div>

    <div v-else-if="!files.length" class="rf__empty" data-testid="session-files-empty">
      <span class="rf__empty-glyph" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M13.6 4H7.5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V9z" /><path d="M13.6 4v5h4.9" /></svg>
      </span>
      <b>Nothing shared yet</b>
      <p v-if="phone && admin">Share slides or a checklist and everyone can open it during the session.</p>
      <p v-else-if="phone">Files the host shares appear here and stay in your library afterwards.</p>
      <p v-else-if="admin">Anything you share here can be opened during the session and stays in their library afterwards.</p>
      <p v-else>If anything is shared during the session, it appears here and in your library.</p>
      <XlButton
        v-if="admin"
        size="sm"
        :loading="uploading"
        :disabled="actionsDisabled"
        data-testid="session-files-share"
        @click="chooseFiles"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><path d="M12 6.2v11.6M6.2 12h11.6" /></svg>
        Share a file
      </XlButton>
    </div>

    <template v-else>
      <ul class="rf__list" aria-label="Files shared in this session">
        <li v-for="resource in files" :key="resource.id" class="rf__row" data-testid="session-file">
          <span v-if="phone" class="rf__fico" :class="`rf__fico--${badgeFor(resource).kind}`" aria-hidden="true" data-testid="session-file-badge">{{ badgeFor(resource).label }}</span>
          <span v-else class="rf__ico" aria-hidden="true">
            <component :is="iconFor(resource)" color="currentColor" size="16" />
          </span>
          <div class="rf__t">
            <b :title="resource.name" data-testid="session-file-name">{{ resource.name }}</b>
            <span data-testid="session-file-meta">{{ phone ? phoneMeta(resource) : typeSize(resource) }}</span>
            <span v-if="manageable(resource) && !phone" class="rf__access">
              <resource-access-tag
                :access="resource.access"
                :members="hubMembers"
                :manageable="contributesOnly ? Boolean(resource.access) : null"
                :book="bookFor(resource, authStore.userData)"
                @manage="accessResource = resource"
              />
            </span>
          </div>
          <button
            v-if="phone"
            type="button"
            class="rf__ib"
            :aria-label="`View ${resource.name}`"
            title="View"
            data-testid="session-file-view"
            @click="viewing = resource"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12z" /><circle cx="12" cy="12" r="3" /></svg>
          </button>
          <button
            type="button"
            :class="phone ? 'rf__ib' : 'rf__dl'"
            :aria-label="`Download ${resource.name}`"
            :title="`Download ${resource.name}`"
            :disabled="downloadingId === resource.id"
            :aria-busy="downloadingId === resource.id ? 'true' : undefined"
            data-testid="session-file-download"
            @click="onDownload(resource)"
          >
            <svg :width="phone ? 17 : 13" :height="phone ? 17 : 13" viewBox="0 0 24 24" fill="none" stroke="currentColor" :stroke-width="phone ? 1.8 : 1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4.2v10.4M8 10.8l4 4 4-4" /><path d="M4.5 19.6h15" /></svg>
          </button>
          <ContentRowMenu
            v-if="editable(resource)"
            :items="menuItems(resource)"
            :label="`More for ${resource.name}`"
            @select="onMenu(resource, $event)"
          />
        </li>
      </ul>

      <!-- Only when there is another page to go to. -->
      <div v-if="lastPage > 1" class="rf__pager" data-testid="session-files-pager">
        <button type="button" aria-label="Previous page" :disabled="page === 1" @click="load(page - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m14.2 18-6-6 6-6" /></svg>
        </button>
        <span>Page {{ page }} of {{ lastPage }}</span>
        <button type="button" aria-label="Next page" :disabled="page === lastPage" @click="load(page + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9.8 18 6-6-6-6" /></svg>
        </button>
      </div>

      <div v-if="admin" class="rf__send">
        <XlButton
          variant="ghost"
          size="sm"
          class="rf__share"
          :loading="uploading"
          :disabled="actionsDisabled"
          data-testid="session-files-share"
          @click="chooseFiles"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><path d="M12 6.2v11.6M6.2 12h11.6" /></svg>
          Share a file
        </XlButton>
      </div>
      <p v-if="!phone" class="rf__note" data-testid="session-files-note">
        Everything shared for this session.
        <template v-if="admin">Anything you add here appears in the policyholder's library afterwards.</template>
        <template v-else>They stay in your library after the session.</template>
      </p>
    </template>

    <Teleport v-if="phone && viewing" :to="viewerHost()">
      <MobileFileViewer
          :resource="viewing"
          :downloading="downloadingId === viewing.id"
          @close="viewing = null"
          @download="onDownload(viewing)"
      />
    </Teleport>

    <!-- The picker Share a file opens; never shown itself, but labelled. -->
    <label v-if="admin" :for="fileInputId" class="rf__sr">Choose files to share with the room</label>
    <input
      v-if="admin"
      :id="fileInputId"
      ref="fileInput"
      type="file"
      class="rf__file"
      :accept="ACCEPT"
      multiple
      tabindex="-1"
      data-testid="session-files-input"
      @change="onFilesChosen"
    />

    <SessionDialog v-if="renaming" title="Rename this file" icon="info" dismissible data-testid="session-file-rename" @close="renaming = null">
      <p>The new name is what everybody sees, here and in their library.</p>
      <template #body>
        <XlField v-model="renameValue" label="File name" :error="renameError" :counted="false" @keyup.enter="saveRename" />
      </template>
      <template #actions>
        <XlButton variant="ghost" @click="renaming = null">Cancel</XlButton>
        <XlButton :loading="busy" @click="saveRename">Save</XlButton>
      </template>
    </SessionDialog>

    <SessionDialog v-if="removing" title="Remove this file?" tone="cr" icon="trash" dismissible data-testid="session-file-remove" @close="removing = null">
      <p>{{ removing.name }} is taken out of the session for everybody, and out of their libraries. This can't be undone.</p>
      <template #actions>
        <XlButton variant="ghost" @click="removing = null">Keep it</XlButton>
        <XlButton variant="danger" :loading="busy" data-testid="session-file-remove-confirm" @click="confirmRemove">Remove</XlButton>
      </template>
    </SessionDialog>

    <manage-access-modal
      v-if="accessResource"
      :resource="accessResource"
      :members="hubMembers"
      allow-apply-to-all
      @close="accessResource = null"
      @saved="handleAccessSaved"
      @apply-all="access => pendingApplyAll = access"
    />

    <apply-to-all-confirm
      v-if="pendingApplyAll"
      :resources="changeable"
      :left-out="leftOut"
      :access="pendingApplyAll"
      :members="hubMembers"
      @cancel="pendingApplyAll = null"
      @confirm="confirmApplyAll"
    />
  </div>
</template>

<style lang="scss" scoped>
/* The prototype's filesPane: .rmppl / .rmrow rows, .ico.b glyphs, .btn.g.sm,
   .rmsend and .rmnote; rmEmpty when there is nothing. Tokens come from the
   panel's [data-xl-gen="northstar"] root; the hex is the prototype's. */
$pur: var(--xl-color-primary, #5E2F78);
$tint: var(--xl-color-primary-container, #F5EFF9);
$tint-3: #D9C6E8;
$line: var(--xl-color-border, #EDE7F0);
$line-2: var(--xl-color-border-subtle, #F6F2F8);
$ink: var(--xl-color-on-surface, #1C1720);
$mut: var(--xl-color-outline, #867D8D);
$faint: var(--xl-color-outline-variant, #B6AEBC);

.rf {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  font-family: var(--xl-font-family);
}

.rf__loading {
  padding: 8px 14px;
}

/* .rmppl */
.rf__list {
  list-style: none;
  margin: 0;
  padding: 4px 14px;
  overflow-y: auto;
  flex: 0 1 auto;
  min-height: 0;
}

/* .rmrow */
.rf__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid $line-2;

  &:last-child {
    border-bottom: none;
  }
}

/* .ico.b at 32px */
.rf__ico {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: grid;
  place-items: center;
  border-radius: 13px;
  background: $tint;
  color: $pur;

  :deep(svg) {
    display: block;
  }
}

/* .rmrow .t: the name may run to two lines before it is cut, and says itself in full on hover */
.rf__t {
  flex: 1;
  min-width: 0;

  b {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.35;
    color: $ink;
  }

  > span {
    display: block;
    font-size: 11.5px;
    line-height: 1.5;
    color: $mut;
  }
}

.rf__access {
  margin-top: 4px;
}

/* .rmppl .rmrow .btn.g.sm */
.rf__dl {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 13px;
  border: 1px solid $line;
  border-radius: 99px;
  background: #fff;
  color: $ink;
  box-shadow: 0 1px 2px rgba(28, 23, 32, 0.04);
  cursor: pointer;
  transition: background-color 0.16s, border-color 0.16s;

  &:hover:not(:disabled) {
    border-color: $tint-3;
    background: $tint;
  }

  &:disabled {
    opacity: 0.5;
    cursor: progress;
  }

  &:focus-visible {
    outline: 2px solid $pur;
    outline-offset: 2px;
  }
}

.rf__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  color: $mut;
  font-variant-numeric: tabular-nums;

  button {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: $mut;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: $line-2;
      color: $ink;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
}

/* .rmsend: Share a file across the foot */
.rf__send {
  display: flex;
  padding: 11px 14px;
  border-top: 1px solid $line;

  .rf__share {
    flex: 1;
  }
}

/* .rmnote */
.rf__note {
  margin: 0;
  padding: 10px 16px 14px;
  font-size: 11.5px;
  line-height: 1.5;
  color: $faint;
}

/* rmEmpty */
.rf__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 30px 24px;
  text-align: center;

  b {
    font-size: 14.5px;
    font-weight: 600;
    color: $ink;
  }

  p {
    max-width: 260px;
    margin: 0 0 10px;
    font-size: 12.5px;
    line-height: 1.55;
    color: $mut;
  }
}

.rf__empty-glyph {
  width: 46px;
  height: 46px;
  margin-bottom: 8px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: $line-2;
  color: $mut;
}

.rf__file {
  display: none;
}

.rf__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* A phone's Files sheet (prototype sheetFiles: .prow, .fico, .ib, btn g blk).
   The sheet gives the side margins. */
.rf--phone {
  .rf__list {
    padding: 0;
  }

  .rf__row {
    gap: 11px;
    padding: 9px 0;
  }

  /* The name on one line: it is cut, the buttons stay. */
  .rf__t b {
    display: block;
    overflow: hidden;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rf__t > span {
    margin-top: 1px;
    font-size: 12px;
  }

  /* the sheet's foot */
  .rf__send {
    margin-top: auto;
    padding: 12px 0 0;

    .rf__share {
      min-height: 44px;
    }
  }
}

.rf__fico {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.03em;

  &--pdf { background: #FCEBEB; color: #A32020; }
  &--ppt { background: #FDF2E2; color: #8A5200; }
  &--doc { background: #E7F1F8; color: #1F5E8C; }
  &--img { background: #E4F3EB; color: #166B48; }
}

.rf__ib {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #F4F1F6;
  color: #514A57;
  cursor: pointer;

  &:active:not(:disabled) {
    background: $tint;
  }

  &:disabled {
    opacity: 0.5;
    cursor: progress;
  }

  &:focus-visible {
    outline: 2px solid $pur;
    outline-offset: 2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rf__dl {
    transition: none;
  }
}
</style>
